import type { ComponentProps } from 'react'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import { DayPicker } from 'react-day-picker'

export type CalendarProps = ComponentProps<typeof DayPicker>

/**
 * Today's day cell: a primary ring with bold primary text, readable in light
 * and dark themes. The ring stays when today is selected (it disappears into
 * the primary fill) and the text color yields to the selected or range style.
 * Calendars that restyle `day` reuse this so today is marked the same way.
 */
export const CALENDAR_DAY_TODAY =
  'font-semibold ring-1 ring-inset ring-primary/60 [&:not([aria-selected])]:text-primary'

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md',
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
        ),
        day_range_start: 'day-range-start',
        day_range_end: 'day-range-end',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: CALENDAR_DAY_TODAY,
        day_outside:
          'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={
        {
          IconLeft: ({ className, ...props }) => (
            <CaretLeftIcon className={cn('h-4 w-4', className)} {...props} />
          ),
          IconRight: ({ className, ...props }) => (
            <CaretRightIcon className={cn('h-4 w-4', className)} {...props} />
          ),
        } as unknown as CalendarProps['components']
      }
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
